import * as React from 'react';
import { ReactNode } from 'react';
import '../css/package-card.sass';
import CampfireButton from './CampfireButton';
import { ActionButton } from './ActionButtons';
import Anchor from './Anchor';
import Card from './Card';
import { HatType } from './Hat';
import MultiActionOption from './MultiActionOption';
import HtmlContent from './HtmlContent';
import SectionMessage, { SectionMessageType } from './SectionMessage';

interface PackageCardProps {
  imgPath?: string;
  name: string;
  description: string;
  children?: ReactNode;
  detailsLinkUrl?: string;
  detailsLinkLabel?: string;
  disclaimer?: string;
  button?: ActionButton;
  theme?: 'night' | 'day';
  id: string;
  hat?: HatType[];
  monthlyTotal?: string;
  additionalDetails?: ReactNode;
  ignoreMobileView?: boolean;
  selectable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onChange?: Function;
  messages?: SectionMessageType[];
  selectableCardAriaLabel?: string;
  selectableCardId?: string;
  ariaLabelledByLabel?: string;
  isRoleButton?: boolean;
}

export default function PackageCard(props: PackageCardProps) {
  const {
    theme = 'day',
    detailsLinkLabel = 'See details',
    button = { type: 'primary' },
    ignoreMobileView = false
  } = props;

  const displayBtn: boolean = !(props.button && props.button.label === '');

  return (
    <>
      {window.innerWidth > 992 || ignoreMobileView ? (
        <div className={`package-card ${theme}`} key={props.id}>
          <Card
            selectable={props.selectable}
            selected={props.selected}
            onChange={(checked: boolean) => {
              if (props.onChange) {
                props.onChange(checked);
              }
            }}
            disabled={props.disabled}
            theme={theme}
            hat={props.hat}
            selectableCardAriaLabel={props.selectableCardAriaLabel}
            selectableCardId={props.selectableCardId}
            ariaLabelledByLabel={props.ariaLabelledByLabel}
            isRoleButton={props.isRoleButton}
          >
            {props.imgPath && (
              <>
                <img className="pkg-img" alt={props.name} src={props.imgPath} />
                <div className="pkg-divider" />
              </>
            )}
            <div className="pkg-name">{props.name}</div>
            <div className="pkg-description">
              <HtmlContent html={props.description} />
            </div>
            {props.children}
            {props.additionalDetails && (
              <div className="pkg-additional-details">
                {props.additionalDetails}
              </div>
            )}
            {displayBtn && props.button && (
              <div className="pkg-btn">
                <CampfireButton
                  name={props.button.label}
                  id={props.id}
                  label={props.button.label}
                  color="water"
                  frosty={!button.type || button.type === 'secondary'}
                  ghost={!button.type || button.type === 'secondary'}
                  theme={theme}
                  onClick={(e: any) => {
                    if (props.button && props.button.onClick !== undefined) {
                      props.button.onClick(e);
                    }
                  }}
                  disabled={props.button.disabled}
                  title={props.button.title}
                />
              </div>
            )}
            {props.messages && props.messages.length > 0 && (
              <div className="pkg-msg">
                <SectionMessage messages={props.messages} />
              </div>
            )}

            {props.detailsLinkUrl && (
              <div className="pkg-link">
                <Anchor
                  href={props.detailsLinkUrl}
                  id={`${props.id}-link`}
                  arialabel={`${detailsLinkLabel} in ${props.name}`}
                  theme={theme}
                  forward
                >
                  {detailsLinkLabel}
                </Anchor>
              </div>
            )}
            {props.disclaimer && (
              <div className="pkg-disclaimer">
                <HtmlContent html={props.disclaimer} />
              </div>
            )}
          </Card>
          {props.selectableCardAriaLabel &&
            !props.isRoleButton &&
            props.ariaLabelledByLabel && (
              <span id={props.id} className="sr-only">
                {props.selectableCardAriaLabel}
              </span>
            )}
        </div>
      ) : (
        <MultiActionOption
          id={props.id}
          name={props.name}
          label={props.name}
          description={props.monthlyTotal}
          href={props.detailsLinkUrl}
          image={props.imgPath}
        />
      )}
    </>
  );
}
